import { Collection, UserResponse, UserCoverStyle, ProfileUserResponse, PublicUserResponse, PublicCollectionResponse } from "@authory-packages/core";
import { useContext, useEffect, useRef, useState } from "react";
import { DomainContext } from "../../context/domainContext";
import { scrollShadowHook } from "../../hooks/scrollShadowHook";
import { RequestHost } from "../../types/headers";
import { getDisplayName } from "../../utils/getDisplayName";
import { V3ProfileContact, V3ProfileSocials } from "../ProfileHero/V3ProfileSocials";
import { ContactUserFormik, V3ContactUser } from "../V3ContactUser";
import {
    PAHContainer,
    PAHLeftSide,
    PAHRightSide,
    PAHWrapper,
    V3PublicHeaderAuthorMeta,
    V3PublicHeaderAuthorName
} from "./V3PublicAuthorHeader.styles";
import { ProfileSocialsContainer } from "../ProfileHero/V4ProfileHero.styles";
import { getAuthoryColorScheme } from "../ProfileHero/V4ProfileHero.utils";
import { EditableSortableType } from "../EditableSortableItems/EditableSortableItems.types";
import { ReadOnlySortableItems } from "../EditableSortableItems/ReadOnlySortableItems";
import { getAboutPagesWithhref } from "../../utils/getAboutPagesWithHref";
import { V3ProfileAvatar } from "../ProfileAvatar";

interface V3PublicAuthorHeaderProps {
    user: UserResponse | ProfileUserResponse | PublicUserResponse;
    onContactUserSubmit: (data: ContactUserFormik) => Promise<void>;
    nonSticky?: boolean;
    collection?: Collection | PublicCollectionResponse;
    showAboutPagesNav?: boolean;
    customBackgroundColor?: string;
    smartShareUrl?: string;
}

export const V3PublicAuthorHeader = ({
    user,
    onContactUserSubmit,
    nonSticky = false,
    collection,
    showAboutPagesNav = false,
    customBackgroundColor,
    smartShareUrl
}: V3PublicAuthorHeaderProps) => {
    const [isStuck, setIsStuck] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), { threshold: 0 });

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    // Contact modal
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [showShadow] = scrollShadowHook();

    // Current Domain
    const requestHostHeader: RequestHost = useContext(DomainContext) ?? RequestHost.MAINDOMAIN;

    // Background and colors
    const colorVariant = getAuthoryColorScheme(!isStuck ? collection?.coverStyle || UserCoverStyle.none : UserCoverStyle.none);
    const pagesWithHref = "aboutPages" in user ? getAboutPagesWithhref(requestHostHeader, false, user, smartShareUrl) : [];

    return (
        <>
            <PAHWrapper $showShadow={showShadow} $nonSticky={nonSticky}>
                <PAHContainer $showAboutPagesNav={showAboutPagesNav}>
                    <PAHLeftSide>
                        {showAboutPagesNav ? (
                            <div>
                                <ReadOnlySortableItems
                                    itemType={EditableSortableType.AboutPages}
                                    scrollCustomColor={"coverStyle" in user && user.coverStyle === UserCoverStyle.color ? user.coverColor : undefined}
                                    colorVariant={colorVariant}
                                    its={pagesWithHref}
                                    activeCol={""}
                                    onItemSelect={() => {}}
                                />
                            </div>
                        ) : (
                            <V3PublicHeaderAuthorMeta>
                                <div>
                                    <V3ProfileAvatar size={"S"} src={user?.avatarUrl[0]?.url} authorName={getDisplayName(user)} />
                                </div>
                                <div>
                                    <V3PublicHeaderAuthorName
                                        $colorVariant={colorVariant}
                                        $skipBaseColor
                                        $customBackgroundColor={customBackgroundColor}
                                    >
                                        {getDisplayName(user)}
                                    </V3PublicHeaderAuthorName>
                                </div>
                            </V3PublicHeaderAuthorMeta>
                        )}
                    </PAHLeftSide>
                    <PAHRightSide>
                        <div>
                            <ProfileSocialsContainer $editMode={false} $profileStyle={"profileStyle" in user ? user.profileStyle : undefined}>
                                <div>
                                    <V3ProfileSocials colorVariant={colorVariant} author={user} customBackgroundColor={customBackgroundColor} />
                                </div>
                                <div>
                                    <V3ProfileContact
                                        openContactModal={() => setContactModalOpen(true)}
                                        colorVariant={colorVariant}
                                        author={user}
                                        isPublicAuthorHeader
                                        customBackgroundColor={customBackgroundColor}
                                    />
                                </div>
                            </ProfileSocialsContainer>
                        </div>
                    </PAHRightSide>
                    {contactModalOpen && (
                        <V3ContactUser
                            modelOpen={contactModalOpen}
                            onCloseHandler={() => setContactModalOpen(false)}
                            userName={getDisplayName(user)}
                            onContactUserSubmit={onContactUserSubmit}
                        />
                    )}
                </PAHContainer>
            </PAHWrapper>
            <div ref={sentinelRef} />
        </>
    );
};
